import React, { useEffect, useId, useRef, useState } from 'react';
import { Plus, Minus, AlertTriangle, Pencil } from 'lucide-react';
import Modal from '../common/Modal';
import { parseQuantity, sanitizeQuantityText, quantityNeedsConfirm, formatSigned } from './stockTakeLogic';
import { useScanBurstGuard } from './scanBurst';

// Set the counted quantity of one line directly, so a single wrong count can be fixed
// without clearing everything.
//   line: { barcode, description?, expected? (IC only), counted }
// onSave(newCount) - 0 clears the line's count.
const LineEditDialog = ({ line, onSave, onClose }) => {
  const inputId = useId();
  const inputRef = useRef(null);
  // Mounted with a key per line, so the field starts from that line's count
  const [text, setText] = useState(() => String(line?.counted ?? ''));
  const [confirmingLarge, setConfirmingLarge] = useState(false);
  useEffect(() => setConfirmingLarge(false), [text]);

  // Scanning is paused while this is open; a scan that lands in the field is undone
  const guardScan = useScanBurstGuard(setText);

  if (!line) return null;

  const hasExpected = typeof line.expected === 'number';
  const value = parseQuantity(text);
  const valid = value !== null;
  const needsConfirm = valid && quantityNeedsConfirm(value, hasExpected ? line.expected : undefined);
  const unchanged = valid && value === line.counted;

  const step = (delta) => setText(String(Math.max(0, (value ?? line.counted) + delta)));

  const save = () => {
    if (!valid || unchanged) {
      if (unchanged) onClose();
      return;
    }
    if (needsConfirm && !confirmingLarge) {
      setConfirmingLarge(true);
      return;
    }
    onSave(value);
  };

  const handleKeyDown = (e) => {
    if (guardScan(e)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Correct count"
      titleIcon={<Pencil size={20} />}
      size="sm"
      tone={confirmingLarge ? 'warning' : 'default'}
      initialFocusRef={inputRef}
      footer={
        <>
          <button type="button" className="st-btn st-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`st-btn ${confirmingLarge ? 'st-btn-warning' : 'st-btn-primary'}`}
            onClick={save}
            disabled={!valid}
          >
            {confirmingLarge ? `Yes, set to ${value}` : 'Save count'}
          </button>
        </>
      }
    >
      <div className="st-qty-item">
        <p className="st-qty-name">{line.description || line.barcode}</p>
        {line.description && <p className="st-qty-barcode">{line.barcode}</p>}
      </div>

      <dl className="st-qty-stats">
        {hasExpected && (
          <div>
            <dt>Expected</dt>
            <dd>{line.expected}</dd>
          </div>
        )}
        <div>
          <dt>Counted now</dt>
          <dd>{line.counted}</dd>
        </div>
      </dl>

      <label className="st-qty-label" htmlFor={inputId}>Counted quantity</label>
      <div className="st-qty-controls">
        <button
          type="button"
          className="st-qty-step"
          onClick={() => step(-1)}
          disabled={(value ?? line.counted) <= 0}
          aria-label="Decrease count by 1"
        >
          <Minus size={24} aria-hidden="true" />
        </button>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          enterKeyHint="done"
          data-scan-capture="true"
          className="st-qty-input"
          value={text}
          onChange={(e) => setText(sanitizeQuantityText(e.target.value))}
          onKeyDown={handleKeyDown}
          onFocus={(e) => e.target.select()}
          aria-invalid={!valid}
        />
        <button
          type="button"
          className="st-qty-step"
          onClick={() => step(1)}
          aria-label="Increase count by 1"
        >
          <Plus size={24} aria-hidden="true" />
        </button>
      </div>

      <p className="st-qty-total" aria-live="polite">
        {!valid ? (
          <span className="st-text-danger">Enter 0 or more</span>
        ) : hasExpected ? (
          <>Difference from expected: <strong>{formatSigned(value - line.expected)}</strong></>
        ) : value === 0 ? (
          <>Saving 0 removes this barcode from the count.</>
        ) : (
          <>Change: <strong>{formatSigned(value - line.counted)}</strong></>
        )}
      </p>

      {confirmingLarge && (
        <div className="st-callout st-callout-warning" role="alert">
          <AlertTriangle size={20} aria-hidden="true" />
          <p>
            {value} is {hasExpected ? `much more than the ${line.expected} expected` : 'a very large count'}.
            Check it, then tap <strong>Yes, set to {value}</strong>.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default LineEditDialog;
